import { AnimatePresence, motion } from 'framer-motion';
import { Copy, WhatsappLogo, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatOrderAsText } from '../lib/order';

// Shown after the customer taps "Checkout via WhatsApp".
// We can't know whether they pressed Send in WhatsApp (or paid), so this only
// reminds them to send the message. The cart is kept until they empty it.
const OrderConfirmationModal = ({ order, whatsappUrl, onBack }) => {
  if (!order) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatOrderAsText(order));
      toast.success('Order copied to clipboard');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const handleReopen = () => {
    if (whatsappUrl) window.open(whatsappUrl, '_blank');
  };

  const itemCount = order.items.reduce((sum, it) => sum + it.quantity, 0);
  const freeCount = order.freeRewards.length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[75] bg-black/85 backdrop-blur-md"
        onClick={onBack}
        data-testid="order-confirmation-backdrop"
      />
      <div className="fixed inset-0 z-[76] flex items-center justify-center p-4 pointer-events-none overflow-y-auto" data-lenis-prevent>
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0, scale: 0.96 }}
          transition={{ type: 'spring', damping: 26, stiffness: 260 }}
          className="pointer-events-auto w-full max-w-lg bg-background border border-border/40 rounded-sm shadow-2xl my-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Finish your order on WhatsApp"
          data-testid="order-confirmation-modal"
        >
          <div className="px-6 pt-8 pb-6 text-center border-b border-border/40">
            <motion.div
              initial={{ scale: 0.6, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.05 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-green-500 text-white"
            >
              <WhatsappLogo size={36} weight="fill" />
            </motion.div>
            <h2 className="font-heading font-black text-3xl text-foreground tracking-tight mb-2">One last step</h2>
            <p className="text-sm text-muted-foreground">
              Your order is <span className="text-foreground font-semibold">not placed yet</span>. Press{' '}
              <span className="text-foreground font-semibold">Send</span> on the message we prepared in WhatsApp to
              place it.
            </p>
            <div
              className="mt-4 inline-flex items-center gap-2 bg-secondary border border-border/40 rounded-sm px-4 py-2"
              data-testid="order-id-display"
            >
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Order</span>
              <span className="font-heading font-bold text-lg text-foreground">{order.id}</span>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Quantity</span>
              <span className="text-foreground font-medium" data-testid="order-quantity">
                {itemCount} {itemCount === 1 ? 'poster' : 'posters'}
                {freeCount > 0 && ` + ${freeCount} free`}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="font-heading font-black text-xl text-foreground">
                ₹{order.grandTotal ?? order.paidTotal}
                <span className="block text-[11px] font-normal text-muted-foreground text-right">
                  {order.deliveryCharge > 0 ? `incl. ₹${order.deliveryCharge} delivery` : 'free delivery'}
                </span>
              </span>
            </div>
          </div>

          <div className="px-6 pb-6 space-y-3">
            <button
              type="button"
              onClick={handleReopen}
              className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-sm font-heading font-bold text-sm transition-all"
              data-testid="reopen-whatsapp-button"
            >
              <WhatsappLogo size={18} weight="fill" />
              Open WhatsApp again
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="w-full flex items-center justify-center gap-2 border border-border/40 hover:bg-secondary py-3 rounded-sm text-sm font-medium text-foreground transition-colors"
              data-testid="copy-order-modal-button"
            >
              <Copy size={16} weight="bold" />
              Copy order summary
            </button>
            <button
              type="button"
              onClick={onBack}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-cart-button"
            >
              <ArrowLeft size={14} weight="bold" />
              Back to cart — your items are saved
            </button>
            <p className="text-[11px] text-center text-muted-foreground pt-1">
              WhatsApp didn&apos;t open? Check your pop-up blocker, or copy the summary and send it to us on WhatsApp.
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OrderConfirmationModal;
